<template>
  <div class="flex flex-col flex-1 min-h-0">
    <div class="flex-1 overflow-y-auto bg-gray-100 p-3 md:p-4" style="min-height: 0;">
      <div class="max-w-2xl mx-auto flex flex-col gap-4">

        <!-- Live preview -->
        <section class="settings-card flex flex-row items-start gap-3" aria-label="Preview">
          <HelpBotAvatar :url="previewUrl" :name="previewName" :size="56" />
          <div class="min-w-0 flex-1">
            <p class="font-medium text-primary break-words" :style="accentText">{{ previewName }}</p>
            <p class="text-xs text-gray-500">{{ personalitySummary }}</p>
            <div class="flex flex-col gap-2 mt-3">
              <p class="preview-bubble preview-bubble-bot text-sm">Hi, I'm {{ previewName }}. How can I help?</p>
              <p class="preview-bubble preview-bubble-user text-sm self-end text-white" :style="userBubbleStyle">How do I add a user?</p>
            </div>
          </div>
        </section>

        <!-- Profile picture -->
        <section class="settings-card" aria-labelledby="help-settings-picture">
          <h3 id="help-settings-picture" class="settings-heading">Profile picture</h3>
          <div class="flex flex-col sm:flex-row gap-4 px-2">
            <div class="flex flex-col items-center gap-2 flex-shrink-0">
              <HelpBotAvatar :url="previewUrl" :name="previewName" :size="112" />
              <p v-if="pending" class="text-xs text-gray-500">Preview, not saved yet</p>
            </div>

            <div class="flex-1 min-w-0">
              <div class="flex flex-row gap-1 mb-2" role="tablist" aria-label="Picture source">
                <button
                  v-for="tab in avatarTabs"
                  :id="`help-avatar-tab-${tab.id}`"
                  :key="tab.id"
                  type="button"
                  role="tab"
                  class="!m-0 !px-3 !py-1"
                  :class="avatarTab === tab.id ? 'bg-white shadow text-primary' : 'text-gray-600'"
                  :aria-selected="avatarTab === tab.id"
                  :aria-controls="`help-avatar-panel-${tab.id}`"
                  @click="avatarTab = tab.id"
                >
                  <font-awesome-icon :icon="tab.icon" class="mr-2"></font-awesome-icon>{{ tab.label }}
                </button>
              </div>

              <div
                v-show="avatarTab === 'upload'"
                id="help-avatar-panel-upload"
                role="tabpanel"
                aria-labelledby="help-avatar-tab-upload"
              >
                <input
                  ref="fileInput"
                  type="file"
                  class="sr-only"
                  :accept="ACCEPTED_UPLOAD_TYPES.join(',')"
                  aria-label="Choose a picture to upload"
                  @change="onFileChosen"
                >
                <button type="button" class="primary !ml-0" :disabled="!!avatarBusy" @click="fileInput?.click()">
                  <font-awesome-icon icon="upload" class="mr-2"></font-awesome-icon>
                  Choose image
                </button>
                <p class="text-xs text-gray-500">PNG, JPEG, WebP, or GIF up to 5 MB. It is cropped to a circle from the centre.</p>
              </div>

              <div
                v-show="avatarTab === 'generate'"
                id="help-avatar-panel-generate"
                role="tabpanel"
                aria-labelledby="help-avatar-tab-generate"
              >
                <label for="help-avatar-prompt" class="text-primary text-sm">Describe the picture</label>
                <div class="settings-box">
                  <textarea
                    id="help-avatar-prompt"
                    v-model="prompt"
                    class="settings-textarea border-b-2 border-gray-300 focus:border-primary"
                    rows="2"
                    :maxlength="MAX_PROMPT_LENGTH"
                    :placeholder="AVATAR_PROMPT_IDEAS[0]"
                    :disabled="!!avatarBusy"
                  ></textarea>
                </div>
                <p class="text-xs text-gray-500 mb-1">Ideas</p>
                <div class="flex flex-wrap gap-1 mb-1">
                  <button
                    v-for="idea in AVATAR_PROMPT_IDEAS"
                    :key="idea"
                    type="button"
                    class="!m-0 !px-2 !py-1 !text-xs !font-normal border border-gray-200 bg-white !rounded-full"
                    :disabled="!!avatarBusy"
                    @click="prompt = idea"
                  >{{ idea }}</button>
                </div>
                <button type="button" class="primary !ml-0" :disabled="!!avatarBusy || prompt.trim().length < 3" @click="generate">
                  <font-awesome-icon
                    :icon="avatarBusy === 'generating' ? 'spinner' : 'wand-magic-sparkles'"
                    :spin="avatarBusy === 'generating'"
                    class="mr-2"
                  ></font-awesome-icon>
                  {{ avatarBusy === 'generating' ? 'Generating…' : 'Generate' }}
                </button>
                <p class="text-xs text-gray-500">Made with Grok Imagine. Generating can take up to a minute.</p>
              </div>

              <div class="flex flex-row flex-wrap items-center mt-2 border-t border-gray-100 pt-1">
                <template v-if="pending">
                  <button type="button" class="success !ml-0" :disabled="!!avatarBusy" @click="savePending">
                    <font-awesome-icon :icon="avatarBusy === 'saving' ? 'spinner' : 'check'" :spin="avatarBusy === 'saving'" class="mr-2"></font-awesome-icon>
                    Use this picture
                  </button>
                  <button type="button" :disabled="!!avatarBusy" @click="pending = null">Discard</button>
                </template>
                <button
                  v-else-if="stored.AvatarUrl"
                  type="button"
                  class="cancel !ml-0"
                  :disabled="!!avatarBusy"
                  @click="removeAvatar"
                >
                  <font-awesome-icon icon="trash" class="mr-2"></font-awesome-icon>
                  Remove picture
                </button>
              </div>
              <p class="text-xs min-h-[1rem]" :class="avatarError ? 'text-red' : 'text-gray-500'" role="status" aria-live="polite">
                {{ avatarError || avatarStatus }}
              </p>
            </div>
          </div>
        </section>

        <!-- Name and personality -->
        <section class="settings-card" aria-labelledby="help-settings-personality">
          <h3 id="help-settings-personality" class="settings-heading">Name and personality</h3>

          <div class="m-2">
            <label for="help-bot-name" class="text-primary text-sm">Name</label>
            <div class="settings-box">
              <input
                id="help-bot-name"
                v-model="draft.BotName"
                class="settings-input border-b-2 border-gray-300 focus:border-primary"
                type="text"
                :maxlength="MAX_NAME_LENGTH"
                :placeholder="DEFAULT_BOT_NAME"
                autocomplete="off"
              >
            </div>
          </div>

          <fieldset class="m-2">
            <legend class="text-primary text-sm mb-1">Personality</legend>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label v-for="option in PERSONALITY_OPTIONS" :key="option.id" class="cursor-pointer">
                <input
                  v-model="draft.Personality"
                  type="radio"
                  class="sr-only peer"
                  name="help-personality"
                  :value="option.id"
                >
                <span class="personality-card peer-checked:border-primary peer-checked:bg-white peer-checked:shadow peer-focus-visible:ring-2 peer-focus-visible:ring-primary">
                  <span class="block text-sm font-medium text-primary">{{ option.label }}</span>
                  <span class="block text-xs text-gray-500">{{ option.description }}</span>
                </span>
              </label>
            </div>
          </fieldset>

          <div class="m-2">
            <label for="help-custom-personality" class="text-primary text-sm">Custom personality (optional)</label>
            <div class="settings-box">
              <textarea
                id="help-custom-personality"
                v-model="draft.CustomPersonality"
                class="settings-textarea border-b-2 border-gray-300 focus:border-primary"
                rows="2"
                :maxlength="MAX_CUSTOM_PERSONALITY_LENGTH"
                placeholder="For example: Explain things like a patient flight instructor."
                aria-describedby="help-custom-personality-count"
              ></textarea>
            </div>
            <p id="help-custom-personality-count" class="text-xs text-gray-500 text-right">
              {{ draft.CustomPersonality.length }}/{{ MAX_CUSTOM_PERSONALITY_LENGTH }}
            </p>
          </div>
          <HelpOptionGroup
            v-if="draft.CustomPersonality.trim()"
            v-model="draft.CustomPersonalityMode"
            legend="Use the custom personality to"
            name="help-personality-mode"
            :options="PERSONALITY_MODE_OPTIONS"
          />
        </section>

        <!-- Responses -->
        <section class="settings-card" aria-labelledby="help-settings-responses">
          <h3 id="help-settings-responses" class="settings-heading">Responses</h3>
          <div class="flex flex-row flex-wrap">
            <HelpOptionGroup v-model="draft.ResponseLength" legend="Length" name="help-length" :options="RESPONSE_LENGTH_OPTIONS" />
            <HelpOptionGroup v-model="draft.Emoji" legend="Emoji" name="help-emoji" :options="EMOJI_OPTIONS" />
          </div>

          <div class="m-2">
            <label for="help-language" class="text-primary text-sm">Language</label>
            <div class="settings-box">
              <select id="help-language" v-model="draft.Language" class="settings-input border-b-2 border-gray-300 focus:border-primary">
                <option v-for="option in LANGUAGE_OPTIONS" :key="option.id" :value="option.id">{{ option.label }}</option>
              </select>
            </div>
          </div>

          <div class="m-2">
            <label for="help-additional-context" class="text-primary text-sm">Instructions and context (optional)</label>
            <div class="settings-box">
              <textarea
                id="help-additional-context"
                v-model="draft.AdditionalContext"
                class="settings-textarea border-b-2 border-gray-300 focus:border-primary"
                rows="3"
                :maxlength="MAX_ADDITIONAL_CONTEXT_LENGTH"
                placeholder="For example: I work in the Design Office. Use British spelling."
                aria-describedby="help-additional-context-hint"
              ></textarea>
            </div>
            <p id="help-additional-context-hint" class="text-xs text-gray-500">
              {{ draft.AdditionalContext.length }}/{{ MAX_ADDITIONAL_CONTEXT_LENGTH }}.
              Style settings apply to written answers about procedures and records. App how-to steps stay as written.
            </p>
          </div>
        </section>

        <!-- Appearance -->
        <section class="settings-card" aria-labelledby="help-settings-appearance">
          <h3 id="help-settings-appearance" class="settings-heading">Appearance</h3>
          <fieldset class="m-2">
            <legend class="text-primary text-sm mb-1">Accent colour</legend>
            <div class="flex flex-row flex-wrap gap-2">
              <label v-for="option in ACCENT_OPTIONS" :key="option.id || 'default'" class="cursor-pointer" v-tooltip="option.label">
                <input
                  v-model="draft.AccentColor"
                  type="radio"
                  class="sr-only peer"
                  name="help-accent"
                  :value="option.id"
                  :aria-label="option.label"
                >
                <span
                  class="accent-swatch peer-checked:ring-2 peer-checked:ring-offset-2 peer-checked:ring-primary peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-primary"
                  :class="option.id ? '' : 'bg-primary'"
                  :style="option.id ? { background: option.id } : null"
                ></span>
              </label>
            </div>
          </fieldset>
        </section>

        <!-- Behaviour -->
        <section class="settings-card" aria-labelledby="help-settings-behaviour">
          <h3 id="help-settings-behaviour" class="settings-heading">Behaviour</h3>
          <label class="settings-toggle">
            <input v-model="draft.Memory" type="checkbox" class="mt-1">
            <span class="block">
              <span class="block text-sm text-primary">Remember the conversation</span>
              <span class="block text-xs text-gray-500">
                Uses earlier messages for follow-ups like "who signed it?". When off, each message is answered on its own.
              </span>
            </span>
          </label>
          <label class="settings-toggle">
            <input
              type="checkbox"
              class="mt-1"
              :checked="draft.Notifications"
              :disabled="!notificationsSupported"
              @change="toggleNotifications"
            >
            <span class="block">
              <span class="block text-sm text-primary">Notify me when {{ previewName }} replies</span>
              <span class="block text-xs text-gray-500">
                {{ notificationsSupported
                  ? 'Shows a browser notification when a reply arrives while this tab or window is in the background.'
                  : 'This browser does not support notifications.' }}
              </span>
              <span v-if="notificationNote" class="block text-xs text-red">{{ notificationNote }}</span>
            </span>
          </label>
        </section>
      </div>
    </div>

    <!-- Footer -->
    <div class="border-t border-gray-100 flex-shrink-0 flex flex-row flex-wrap items-center px-2 bg-white">
      <button type="button" :disabled="saving" @click="resetToDefaults" v-tooltip="'Your picture is not changed'">
        <font-awesome-icon icon="rotate-left" class="mr-2"></font-awesome-icon>
        Reset to defaults
      </button>
      <p class="flex-grow text-xs px-2" :class="formError ? 'text-red' : 'text-gray-500'" role="status" aria-live="polite">
        {{ formError || savedMessage || (dirty ? 'Unsaved changes' : '') }}
      </p>
      <button type="button" :class="{ cancel: dirty }" :disabled="saving" @click="close">
        {{ dirty ? 'Cancel' : 'Back to chat' }}
      </button>
      <button type="button" class="primary" :disabled="!dirty || saving || !userId" @click="save">
        <font-awesome-icon :icon="saving ? 'spinner' : 'check'" :spin="saving" class="mr-2"></font-awesome-icon>
        Save
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { appConfig, callable, db } from '../../../db.js';
import HelpBotAvatar from './HelpBotAvatar.vue';
import HelpOptionGroup from './HelpOptionGroup.vue';
import {
  ACCENT_OPTIONS,
  ACCEPTED_UPLOAD_TYPES,
  AVATAR_PROMPT_IDEAS,
  DEFAULT_BOT_NAME,
  DEFAULT_SETTINGS,
  EMOJI_OPTIONS,
  LANGUAGE_OPTIONS,
  MAX_ADDITIONAL_CONTEXT_LENGTH,
  MAX_CUSTOM_PERSONALITY_LENGTH,
  MAX_NAME_LENGTH,
  MAX_PROMPT_LENGTH,
  PERSONALITY_MODE_OPTIONS,
  PERSONALITY_OPTIONS,
  RESPONSE_LENGTH_OPTIONS,
  editableSettings,
  resolveSettings,
  squareCrop,
  uploadProblem,
} from './helpChatSettings.mjs';

const emit = defineEmits(['close']);

const MAX_SAVED_IMAGE_BYTES = 1.8 * 1024 * 1024;

const store = useStore();

const userId = computed(() => {
  const id = store.getters.userID || store.state.user?.Basic?.key;
  return id && id !== 'Unknown' ? id : null;
});

const stored = computed(() => resolveSettings(store.state.helpChatSettings));
const draft = reactive(editableSettings(store.state.helpChatSettings));
const savedSnapshot = ref(JSON.stringify(editableSettings(store.state.helpChatSettings)));
const dirty = computed(() => JSON.stringify(editableSettings(draft)) !== savedSnapshot.value);

const saving = ref(false);
const formError = ref('');
const savedMessage = ref('');

watch(() => store.state.helpChatSettings, (value) => {
  if (!dirty.value) Object.assign(draft, editableSettings(value));
  savedSnapshot.value = JSON.stringify(editableSettings(value));
}, { deep: true });

watch(dirty, (isDirty) => {
  if (!isDirty) return;
  savedMessage.value = '';
  formError.value = '';
});

const previewName = computed(() => draft.BotName.replace(/\s+/g, ' ').trim() || DEFAULT_BOT_NAME);

const personalitySummary = computed(() => {
  const preset = PERSONALITY_OPTIONS.find((option) => option.id === draft.Personality)?.label || '';
  if (!draft.CustomPersonality.trim()) return preset;
  return draft.CustomPersonalityMode === 'override' ? 'Custom personality' : `${preset}, plus your instructions`;
});

const accentText = computed(() => (draft.AccentColor ? { color: draft.AccentColor } : null));
const userBubbleStyle = computed(() => (draft.AccentColor ? { background: draft.AccentColor } : null));

async function save() {
  if (!userId.value || saving.value) return;
  saving.value = true;
  formError.value = '';
  try {
    const values = editableSettings(draft);
    const updates = { User: userId.value };
    for (const [key, value] of Object.entries(values)) updates[`Settings/${key}`] = value;
    await db.update(`HelpChats/${userId.value}`, updates);
    Object.assign(draft, values);
    savedSnapshot.value = JSON.stringify(values);
    savedMessage.value = 'Settings saved.';
  } catch (error) {
    console.error('[HelpChat] Failed to save settings', error);
    formError.value = 'Your settings could not be saved. Please try again.';
  } finally {
    saving.value = false;
  }
}

function resetToDefaults() {
  Object.assign(draft, { ...DEFAULT_SETTINGS });
}

function close() {
  emit('close');
}

// Notifications

const notificationsSupported = typeof window !== 'undefined' && 'Notification' in window;
const notificationNote = ref('');

async function toggleNotifications(event) {
  notificationNote.value = '';
  if (!event.target.checked) {
    draft.Notifications = false;
    return;
  }
  let permission = Notification.permission;
  if (permission === 'default') permission = await Notification.requestPermission();
  draft.Notifications = permission === 'granted';
  event.target.checked = draft.Notifications;
  if (!draft.Notifications) {
    notificationNote.value = 'Notifications are blocked for this site. Allow them in your browser settings, then try again.';
  }
}

// Profile picture

const avatarTabs = [
  { id: 'upload', label: 'Upload', icon: 'upload' },
  { id: 'generate', label: 'Generate', icon: 'wand-magic-sparkles' },
];
const avatarTab = ref('upload');
const fileInput = ref(null);
const prompt = ref('');
const pending = ref(null);
const avatarBusy = ref('');
const avatarError = ref('');
const avatarStatus = ref('');
const avatarCallable = callable('helpChatAvatar');
const databaseURL = appConfig?.firebaseConfig?.databaseURL;

const previewUrl = computed(() => pending.value?.image || stored.value.AvatarUrl);

watch(pending, () => {
  avatarStatus.value = '';
});

function callableMessage(error, fallback) {
  const message = typeof error?.message === 'string' ? error.message.trim() : '';
  if (!message || /^(internal|unavailable|deadline-exceeded|unknown)$/i.test(message)) return fallback;
  return message;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The image could not be read.'));
    image.src = src;
  });
}

async function squareImage(src) {
  const image = await loadImage(src);
  if (!image.naturalWidth || !image.naturalHeight) throw new Error('The image has no size.');
  const { sx, sy, side, size } = squareCrop(image.naturalWidth, image.naturalHeight);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  canvas.getContext('2d').drawImage(image, sx, sy, side, side, 0, 0, size, size);
  const png = canvas.toDataURL('image/png');
  return png.length * 0.75 <= MAX_SAVED_IMAGE_BYTES ? png : canvas.toDataURL('image/jpeg', 0.9);
}

async function onFileChosen(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  avatarError.value = '';
  const problem = uploadProblem(file);
  if (problem) {
    avatarError.value = problem;
    return;
  }
  avatarBusy.value = 'processing';
  const objectUrl = URL.createObjectURL(file);
  try {
    pending.value = { image: await squareImage(objectUrl), source: 'upload' };
  } catch (error) {
    console.error('[HelpChat] Failed to read avatar image', error);
    avatarError.value = 'That image could not be read. Try a different file.';
  } finally {
    URL.revokeObjectURL(objectUrl);
    avatarBusy.value = '';
  }
}

async function generate() {
  const text = prompt.value.trim();
  if (text.length < 3 || avatarBusy.value) return;
  avatarBusy.value = 'generating';
  avatarError.value = '';
  try {
    const { data } = await avatarCallable({ action: 'generate', prompt: text, databaseURL });
    pending.value = { image: await squareImage(data.image), source: 'generated', prompt: text };
  } catch (error) {
    console.error('[HelpChat] Failed to generate avatar', error);
    avatarError.value = callableMessage(error, 'The picture could not be generated. Please try again.');
  } finally {
    avatarBusy.value = '';
  }
}

async function savePending() {
  if (!pending.value || avatarBusy.value) return;
  avatarBusy.value = 'saving';
  avatarError.value = '';
  try {
    const { image, source, prompt: imagePrompt } = pending.value;
    await avatarCallable({ action: 'save', image, source, prompt: imagePrompt, databaseURL });
    pending.value = null;
    avatarStatus.value = 'Picture saved.';
  } catch (error) {
    console.error('[HelpChat] Failed to save avatar', error);
    avatarError.value = callableMessage(error, 'The picture could not be saved. Please try again.');
  } finally {
    avatarBusy.value = '';
  }
}

async function removeAvatar() {
  if (avatarBusy.value) return;
  avatarBusy.value = 'removing';
  avatarError.value = '';
  try {
    await avatarCallable({ action: 'remove', databaseURL });
    avatarStatus.value = 'Picture removed.';
  } catch (error) {
    console.error('[HelpChat] Failed to remove avatar', error);
    avatarError.value = callableMessage(error, 'The picture could not be removed. Please try again.');
  } finally {
    avatarBusy.value = '';
  }
}
</script>

<style scoped>
.settings-card {
  padding: 0.5rem;
  border: 1px solid #f3f4f6;
  border-radius: 0.25rem;
  background: white;
  box-shadow: 0 1px 3px rgb(0 0 0 / 6%);
}

.settings-heading {
  padding: 0.5rem;
  font-size: 0.875rem;
  font-weight: 700;
}

.settings-box {
  display: flex;
  margin: 0.25rem 0;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  background: linear-gradient(to bottom right, #f3f4f6, #eceef1);
  box-shadow: inset 0 2px 4px rgb(0 0 0 / 6%);
}

.settings-input,
.settings-textarea {
  width: 100%;
  background: transparent;
  outline: none;
  font-size: 0.875rem;
}

.settings-input {
  line-height: 36px;
  height: 38px;
}

.settings-textarea {
  padding: 0.4rem 0;
  line-height: 1.4;
  resize: vertical;
}

.personality-card {
  display: block;
  height: 100%;
  padding: 0.5rem 0.75rem;
  border: 2px solid #f3f4f6;
  border-radius: 0.25rem;
  background: #f9fafb;
  transition: border-color 0.15s, background-color 0.15s;
}

.personality-card:hover {
  background: white;
}

.accent-swatch {
  display: block;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  box-shadow: inset 0 0 0 2px rgb(255 255 255 / 60%);
}

.settings-toggle {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 0.6rem;
  margin: 0.5rem;
  cursor: pointer;
}

.preview-bubble {
  max-width: 85%;
  padding: 0.5rem 0.8rem;
  border-radius: 1rem;
}

.preview-bubble-bot {
  align-self: flex-start;
  border: 1px solid #f3f4f6;
  border-bottom-left-radius: 0.3rem;
  background: #f9fafb;
}

.preview-bubble-user {
  border-bottom-right-radius: 0.3rem;
  background: #b9cde5;
}
</style>
